import { useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

type useFAQViewedEventProps = {
	spaceKey: string;
	questionCount: number;
};

type ViewedThreshold = 'partial' | 'half' | 'full';

const THRESHOLDS = [0, 0.5, 1.0] as const;

const getThresholdLabel = (ratio: number): ViewedThreshold => {
	if (ratio >= 1.0) {
		return 'full';
	}
	if (ratio >= 0.5) {
		return 'half';
	}
	return 'partial';
};

// Fires "module viewed" analytics events when the FAQ section scrolls into view
// at increasing visibility thresholds (0%, 50%, 100%). Each threshold fires at
// most once per page load so we can measure how far users scroll through the module.
export const useFAQViewedEvent = ({ spaceKey, questionCount }: useFAQViewedEventProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const sectionRef = useRef<HTMLElement>(null);
	const firedThresholdsRef = useRef<Set<ViewedThreshold>>(new Set());

	// questionCount may change when refetching data, but we only need the latest value
	// when the intersection fires — not as a trigger to re-observe.
	const questionCountRef = useRef(questionCount);
	questionCountRef.current = questionCount;

	useEffect(() => {
		const node = sectionRef.current;
		if (!node) {
			return;
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				if (!entry.isIntersecting) {
					return;
				}

				const label = getThresholdLabel(entry.intersectionRatio);

				const thresholdLabels: ViewedThreshold[] = ['partial', 'half', 'full'];
				const currentIndex = thresholdLabels.indexOf(label);

				for (let i = 0; i <= currentIndex; i++) {
					const threshold = thresholdLabels[i];
					if (!firedThresholdsRef.current.has(threshold)) {
						firedThresholdsRef.current.add(threshold);
						createAnalyticsEvent({
							type: 'sendUIEvent',
							data: {
								action: 'viewed',
								actionSubject: 'module',
								actionSubjectId: 'faq',
								source: 'smartSpaceOverview',
								attributes: {
									moduleType: 'faq',
									spaceKey,
									questionCount: questionCountRef.current,
									threshold,
								},
							},
						}).fire();
					}
				}

				if (firedThresholdsRef.current.size === thresholdLabels.length) {
					observer.disconnect();
				}
			},
			{ threshold: [...THRESHOLDS] },
		);

		observer.observe(node);

		return () => {
			observer.disconnect();
		};
	}, [spaceKey, createAnalyticsEvent]);

	return { sectionRef };
};
